<script setup lang="ts">
import { computed } from "vue";
import { useProductPrice, usePrice, useSessionContext } from "#imports";
import SwSharedPrice from "../../shared/SharedPrice.vue";

const props = defineProps<{
  product: any; // Replace with proper type
  alignment?: string;
}>();

const { taxState } = useSessionContext();
const { unitPrice, price, tierPrices, isListPrice } = useProductPrice(
  computed(() => props.product)
);
const regulationPrice = computed(() => price.value?.regulationPrice?.price);
const { getFormattedPrice } = usePrice();

const productNumber = computed(() => props.product?.productNumber);
</script>

<template>
  <div
    class="product-basic-info"
    :class="{
      'justify-start': alignment === 'flex-start',
      'justify-end': alignment === 'flex-end',
      'justify-center': alignment === 'center',
    }"
  >
    <!-- Product Name -->
    <h1 class="font-bold text-dark-bg leading-tight mb-2 text-2xl md:text-40px">
      {{ product.translated?.name }}
    </h1>

    <!-- Order Number -->
    <p class="text-sm text-davy-grey font-normal">
      {{ $t("productDetails.productNumber") }}:{{ productNumber }}
    </p>

    <div class="chf mt-6 mb-8 md:my-8 space-y-1">
      <!-- CHF Number -->
      <div v-if="tierPrices.length <= 1">
        <SwSharedPrice
          v-if="unitPrice"
          class="text-2xl md:text-xl font-bold text-black md:text-maximum-red"
          :class="{
            'text-maximum-red': isListPrice,
          }"
          :value="unitPrice"
        />
        <span
          v-if="isListPrice"
          class="text-sm font-normal text-arsenic flex items-center mt-1"
        >
          {{ $t("product.normalPrice") }}:
          <SwSharedPrice
            class="text-sm font-normal text-arsenic line-through"
            :value="price?.listPrice?.price"
          />
        </span>
        <div v-if="regulationPrice" class="text-xs flex text-secondary-500">
          {{ $t("productDetails.previously") }}
          <SwSharedPrice class="ml-1" :value="regulationPrice" />
        </div>
      </div>
      <div v-else>
        <table class="border-collapse table-auto w-full text-sm mb-8">
          <thead>
            <tr>
              <th
                class="border-b dark:border-secondary-600 font-medium p-4 pl-8 pt-0 pb-3 text-secondary-600 dark:text-secondary-200 text-left"
              >
                {{ $t("productDetails.amount") }}
              </th>
              <th
                class="border-b dark:border-secondary-600 font-medium p-4 pr-8 pt-0 pb-3 text-secondary-600 dark:text-secondary-200 text-left"
              >
                {{ $t("productDetails.price.label") }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-secondary-800">
            <tr v-for="(tierPrice, index) in tierPrices" :key="tierPrice.label">
              <td
                class="border-b border-secondary-100 dark:border-secondary-700 p-4 pl-8 font-medium text-secondary-500 dark:text-secondary-400"
              >
                <span v-if="index < tierPrices.length - 1">
                  {{ $t("productDetails.to") }} </span
                ><span v-else>{{ $t("productDetails.from") }}</span>
                {{ tierPrice.quantity }}
              </td>
              <td
                class="border-b border-secondary-100 dark:border-secondary-700 p-4 pr-8 font-medium text-current-500 dark:text-secondary-400"
              >
                {{ getFormattedPrice(tierPrice.unitPrice) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-sm font-normal text-arsenic">
        <template v-if="taxState === 'gross'">
          {{ $t("productDetails.pricesIncl") }}
        </template>
        <template v-else> {{ $t("productDetails.pricesExcl") }} </template>
      </p>
    </div>
  </div>
</template>
